import { Dispatch } from 'react-redux';

import { Action } from '../../actions';
import { StoreState } from '../../reducers';
import ApiUtils from '../../utils/api';
import { Api } from '../../typings/api';
import { createNotification } from '../NotificationBar/actions';
import { updateCredentials } from '../MultiAppAuthentication/actions';
import LocalStorageManager from '../../utils/localStorage/manager';
import UserAgentUtils from '../../utils/userAgent';
import { ProfilePageFields } from './reducer';
import { PositionAndSize } from '../Editor/utils/helpers';

export enum ProfileActionKeys {
	SET_DATA = '@@profile/SET_DATA',
	SET_IS_LOADING = '@@profile/SET_IS_LOADING',
	ADD_USER_AVATAR = '@@profile/ADD_USER_AVATAR',
	UPDATE_USER_AVATAR = '@@profile/UPDATE_USER_AVATAR',
	REMOVE_USER_AVATAR = '@@profile/REMOVE_USER_AVATAR',
	RESET_USER_AVATAR = '@@profile/RESET_USER_AVATAR',
	ENABLE_CRM_PROVIDER = '@@profile/ENABLE_CRM_PROVIDER',
}

interface SetProfileDataAction extends Action {
	type: ProfileActionKeys.SET_DATA;
	data: Api.ProfileData;
}

interface SetProfileIsLoadingAction extends Action {
	type: ProfileActionKeys.SET_IS_LOADING;
	isLoading: boolean;
}

interface AddUserAvatarAction extends Action {
	type: ProfileActionKeys.ADD_USER_AVATAR;
	file: File;
	isCorrectFileType: boolean;
}

interface UpdateUserAvatarAction extends Action {
	type: ProfileActionKeys.UPDATE_USER_AVATAR;
	data: PositionAndSize;
}

interface RemoveUserAvatarAction extends Action {
	type: ProfileActionKeys.REMOVE_USER_AVATAR;
}

interface ResetUserAvatarAction extends Action {
	type: ProfileActionKeys.RESET_USER_AVATAR;
}

interface EnableCrmProviderAction extends Action {
	type: ProfileActionKeys.ENABLE_CRM_PROVIDER;
	crmProvider: Api.CRMProviders;
}

export type ProfileAction =
	SetProfileDataAction |
	SetProfileIsLoadingAction |
	AddUserAvatarAction |
	UpdateUserAvatarAction |
	RemoveUserAvatarAction |
	ResetUserAvatarAction |
	EnableCrmProviderAction;

export const setProfileData = (data: Api.ProfileData): SetProfileDataAction => ({
	type: ProfileActionKeys.SET_DATA,
	data,
});

export const setProfileIsLoading = (isLoading: boolean): SetProfileIsLoadingAction => ({
	type: ProfileActionKeys.SET_IS_LOADING,
	isLoading,
});

export const addUserAvatar = (file: File, isCorrectFileType: boolean) => (dispatch: Dispatch<StoreState>): void => {
	if (!isCorrectFileType) {
		return dispatch(createNotification('error.wrong_file_type'));
	}

	dispatch({
		type: ProfileActionKeys.ADD_USER_AVATAR,
		file,
		isCorrectFileType,
	});
};

export const updateUserAvatar = (data: PositionAndSize): UpdateUserAvatarAction => ({
	type: ProfileActionKeys.UPDATE_USER_AVATAR,
	data,
});

export const removeUserAvatar = (): RemoveUserAvatarAction => ({
	type: ProfileActionKeys.REMOVE_USER_AVATAR,
});

export const resetUserAvatar = (): ResetUserAvatarAction => ({
	type: ProfileActionKeys.RESET_USER_AVATAR,
});

const enableCrmProvider = (crmProvider: Api.CRMProviders): EnableCrmProviderAction => ({
	type: ProfileActionKeys.ENABLE_CRM_PROVIDER,
	crmProvider,
});

export const getProfileData = (clientId: number) => async (dispatch: Dispatch<StoreState>): Promise<void> => {
	const isOnline = await UserAgentUtils.isOnlinePromise();

	if (!isOnline) {
		return;
	}

	try {
		dispatch(setProfileIsLoading(true));
		const data = await ApiUtils.getProfileData(clientId);
		dispatch(setProfileData(data));
	} catch (error) {
		dispatch(createNotification('error.fetch_profile_data_error'));
	} finally {
		dispatch(setProfileIsLoading(false));
	}
};

export const saveProfileData = (fields: ProfilePageFields) => async (dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<void> => {
	const {
		profile: {
			id,
			clientId,
			userAvatar,
		},
	} = getState();

	if (Object.keys(fields).length === 0 && !userAvatar.isEdited) {
		return;
	}

	try {
		dispatch(setProfileIsLoading(true));
		const data = await ApiUtils.editProfileData(id, clientId, fields, userAvatar);

		if (fields.password && fields.confirmPassword) {
			if (UserAgentUtils.isMobileApp) {
				dispatch(updateCredentials({ password: fields.password }));
			} else {
				// in web version update password in localStorage
				if (LocalStorageManager.getPassword()) {
					LocalStorageManager.setPassword(fields.password);
				}
			}
		}

		dispatch(setProfileData(data));
	} catch (error) {
		dispatch(createNotification('error.save_profile_data_error'));
	} finally {
		dispatch(setProfileIsLoading(false));
	}
};

export const activateSalesforce = (
	clientId: string,
	clientSecret: string,
	callback: () => void,
) => async (dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<void> => {
	try {
		const { profile: { crmProviders } } = getState();
		const crmProvidersToSave = crmProviders.map(item => (
			item.provider === Api.CRMProviders.SALESFORCE
				? { ...item, clientId, clientSecret }
				: item
		));
		await dispatch(saveProfileData({
			crmProviders: crmProvidersToSave,
		} as ProfilePageFields));

		// we have to enable Salesforce provider locally as server returns 'enabled: false' in response. It is set to true on server only after successful integration.
		// without this action checkbox in CRMSelector jumps to prevously selected option.
		dispatch(enableCrmProvider(Api.CRMProviders.SALESFORCE));
		callback();
	} catch (error) {
		dispatch(createNotification('error.save_profile_data_error'));
	}
};



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/containers/ProfilePage/actions.ts